// recipient-web entry point.
//
// Served from the dedicated `link.blockhaven.health` bucket at
// `/s/{token}`. The token is the path suffix; we extract it, ask
// share-bundle-service (on `share.*`, a different origin) which kind of
// token it is (link-share PDF or WebRTC), then dispatch to the right
// renderer.
//
// After the 2026-06-11 share.*/link.* split this app is NOT same-origin
// with its API: the API host is pinned to `__API_BASE__` and the
// signaling host to `__SIGNALING_BASE__`, both injected at build time
// via esbuild `--define`. See SPEC-INFRA-LINK-SHARE-RECIPIENT-WEB-CONTRACT.
//
// Per Linear DEV2-419.

import { discover, DiscoverError, type DiscoverResponse } from "./discover.js";
import { renderPdfView } from "./pdf-view.js";
import { renderShareSessionView } from "./share-session.js";

/**
 * The share-bundle-service base URL: the build-time `__API_BASE__` constant
 * (`share.blockhaven.health` in prod) — the app is served from `link.*` and is
 * not same-origin with the API. Never includes a trailing slash.
 *
 * `?api=` overrides it only in the local watch build (`__URL_OVERRIDES__`).
 * Every published build sets that define to false, so esbuild removes the
 * branch and the bundle reads no query parameter at all: an attacker-supplied
 * `?api=` would otherwise receive the share token from a real link.
 */
function resolveApiBaseUrl(): string {
  if (__URL_OVERRIDES__) {
    const override = new URL(window.location.href).searchParams.get("api");
    if (override) return override.replace(/\/+$/, "");
  }
  return __API_BASE__.replace(/\/+$/, "");
}

/**
 * Base URL of the clinician longitudinal dashboard: the build-time
 * `__DASHBOARD_BASE__` constant, a pinned define rather than one derived from
 * the serving host. Never includes a trailing slash.
 *
 * `?dash=` overrides it only in the local watch build (`__URL_OVERRIDES__`).
 * The redirect below carries the URL fragment, which holds the share's
 * decryption key, so in a published build an override would hand that key to
 * any origin named in the query. Published builds set the define to false and
 * esbuild removes the branch.
 */
function resolveDashboardBaseUrl(): string {
  if (__URL_OVERRIDES__) {
    const override = new URL(window.location.href).searchParams.get("dash");
    if (override) return override.replace(/\/+$/, "");
  }
  return __DASHBOARD_BASE__.replace(/\/+$/, "");
}

/** Extract `{token}` from `/s/{token}` or `/share/{token}` URL paths. */
function extractToken(): string | null {
  const path = window.location.pathname;
  const m = path.match(/^\/s\/([^/?#]+)$/) ?? path.match(/^\/share\/([^/?#]+)$/);
  return m?.[1] ?? null;
}

function showError(root: HTMLElement, title: string, body: string): void {
  root.innerHTML = `
    <div class="card error">
      <h1>${escapeHtml(title)}</h1>
      <p>${escapeHtml(body)}</p>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

async function bootstrap(): Promise<void> {
  const root = document.getElementById("root");
  if (!root) {
    return;
  }

  const token = extractToken();
  if (!token) {
    showError(
      root,
      "Invalid share link",
      "This URL doesn't look like a BlockHaven share link.",
    );
    return;
  }

  const apiBaseUrl = resolveApiBaseUrl();
  let metadata: DiscoverResponse;
  try {
    metadata = await discover(apiBaseUrl, token);
  } catch (e) {
    if (e instanceof DiscoverError) {
      const title =
        e.code === "not_found"
          ? "Share link not found"
          : e.code === "gone"
            ? "Share link unavailable"
            : e.code === "consumed"
              ? "Already viewed"
              : "Couldn't open this share link";
      showError(root, title, e.message);
      return;
    }
    showError(
      root,
      "Couldn't reach BlockHaven",
      "We had trouble looking up this share link. Check your connection and try again.",
    );
    return;
  }

  if (metadata.kind === "link") {
    // Static document share (PDF + optional FHIR) — unchanged.
    renderPdfView(root, { apiBaseUrl, token, metadata });
  } else if (metadata.data_classes?.includes("longitudinal_record")) {
    // The "share with my doctor" longitudinal share → the live clinician
    // dashboard, which runs its OWN presence + patient approval + decrypt and
    // renders the longitudinal view. The CEK stays in the URL fragment (never
    // sent to a server); replace() so back doesn't bounce here. Dispatch on the
    // data class, NOT the transport kind — other presence (webrtc) shares (e.g.
    // the pre-visit data package) must keep using the share-session flow below.
    const dash = resolveDashboardBaseUrl();
    window.location.replace(
      `${dash}/provider/longitudinal/shared/${encodeURIComponent(token)}${window.location.hash}`,
    );
  } else {
    // Other presence-gated shares (pre-visit data package, tap-to-share) —
    // unchanged download-on-approval flow.
    void renderShareSessionView(root, { apiBaseUrl, token, metadata });
  }
}

void bootstrap();
